import "@/App.css";
import axios from "axios";
import {
  Bell,
  ClipboardList,
  Home,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  MessageSquare,
  Search,
  Salad,
  TestTube2,
  User,
  Users,
} from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const GOAL_OPTIONS = [
  "weight loss",
  "muscle gain",
  "diabetes management",
  "pcod management",
  "thyroid management",
  "cholesterol management",
  "general wellness",
];
const DIET_OPTIONS = ["Vegetarian", "Vegan", "Non-Vegetarian", "Eggetarian", "Jain"];
const ACTIVITY_OPTIONS = ["Sedentary", "Lightly Active", "Moderately Active", "Very Active"];
const GENDER_OPTIONS = ["Female", "Male", "Other"];
const MEAL_TYPES = ["Breakfast", "Lunch", "Snack", "Dinner"];
const MEALS_SKIPPED_OPTIONS = ["Never", "Sometimes", "Always"];
const HERO_IMAGE = "https://images.pexels.com/photos/2027696/pexels-photo-2027696.jpeg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-rx-7/artifacts/v87juauk_android%20icon%20tfp.png";
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "sudarshanathigle312@gmail.com";

const AuthContext = createContext(null);

const getStoredToken = () => window.localStorage.getItem("tfp-token") || "";

const buildHeaders = (token, extra = {}) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  ...extra,
});

const friendlyDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const classNames = (...classes) => classes.filter(Boolean).join(" ");

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStoredToken());
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const persistToken = useCallback((value) => {
    if (value) {
      window.localStorage.setItem("tfp-token", value);
    } else {
      window.localStorage.removeItem("tfp-token");
    }
    setToken(value || "");
  }, []);

  const authRequest = useCallback(
    async (method, url, data, config = {}) => {
      const headers = buildHeaders(token, config.headers || {});
      const response = await api.request({ method, url, data, ...config, headers });
      return response.data;
    },
    [token],
  );

  const refreshUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setInitializing(false);
      return;
    }
    try {
      const response = await api.get("/auth/me", { headers: buildHeaders(token) });
      setUser(response.data.user);
    } catch {
      persistToken("");
      setUser(null);
    } finally {
      setInitializing(false);
    }
  }, [persistToken, token]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(
    async (payload) => {
      const response = await api.post("/auth/login", payload);
      persistToken(response.data.token);
      setUser(response.data.user);
      return response.data.user;
    },
    [persistToken],
  );

  const signup = useCallback(async (payload) => api.post("/auth/signup", payload), []);
  const forgotPassword = useCallback(async (payload) => (await api.post("/auth/forgot-password", payload)).data, []);
  const resetPassword = useCallback(async (payload) => (await api.post("/auth/reset-password", payload)).data, []);

  const logout = useCallback(() => {
    persistToken("");
    setUser(null);
  }, [persistToken]);

  const value = useMemo(
    () => ({ token, user, initializing, login, signup, forgotPassword, resetPassword, logout, refreshUser, authRequest }),
    [authRequest, forgotPassword, initializing, login, logout, refreshUser, resetPassword, signup, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const LoadingScreen = ({ label = "Loading your dashboard..." }) => (
  <div className="min-h-screen bg-[#F8FAF8] flex items-center justify-center px-6" data-testid="loading-screen">
    <div className="rounded-3xl border border-[#D1D5DB] bg-white px-8 py-10 text-center shadow-sm max-w-md w-full">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#E8F5E9] text-[#2E7D32]">
        <LoaderCircle className="h-7 w-7 animate-spin" />
      </div>
      <h2 className="text-2xl font-semibold text-slate-900" data-testid="loading-label">{label}</h2>
    </div>
  </div>
);

const StatusPill = ({ value, testId }) => {
  const tone = value === "active"
    ? "bg-emerald-100 text-emerald-700"
    : value === "pending"
      ? "bg-amber-100 text-amber-700"
      : value === "approved"
        ? "bg-green-100 text-green-700"
        : "bg-slate-100 text-slate-700";
  return <span className={classNames("rounded-full px-3 py-1 text-xs font-semibold capitalize", tone)} data-testid={testId}>{value || "draft"}</span>;
};

const PageHeader = ({ eyebrow, title, description, actions }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-testid={`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-header`}>
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4CAF50]">{eyebrow}</p>
      <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
      <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">{description}</p>
    </div>
    {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
  </div>
);

const MetricCard = ({ label, value, subtext, testId, onClick }) => (
  <Card className={classNames("border-[#D1D5DB] shadow-sm", onClick ? "cursor-pointer transition-colors duration-200 hover:border-[#4CAF50] hover:bg-[#FCFDFC]" : "")} data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (event) => { if (event.key === "Enter" || event.key === " ") onClick(); } : undefined}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-slate-600">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-4xl font-bold text-slate-900">{value}</div>
      <p className="mt-2 text-sm text-slate-500">{subtext}</p>
    </CardContent>
  </Card>
);

const FormField = ({ label, children, testId }) => (
  <label className="space-y-2" data-testid={testId}>
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    {children}
  </label>
);

const ChoiceGrid = ({ options, selected, onToggle, multi = false, testPrefix }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {options.map((option) => {
      const active = multi ? selected.includes(option) : selected === option;
      return (
        <button
          key={option}
          type="button"
          onClick={() => onToggle(option)}
          className={classNames(
            "rounded-2xl border px-4 py-4 text-left text-sm font-medium transition-colors duration-200",
            active ? "border-[#4CAF50] bg-[#E8F5E9] text-[#2E7D32]" : "border-[#D1D5DB] bg-white text-slate-700 hover:border-[#4CAF50]",
          )}
          data-testid={`${testPrefix}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {option}
        </button>
      );
    })}
  </div>
);

const RadioChoiceList = ({ options, selected, onChange, name, testPrefix }) => (
  <div className="grid gap-3" role="radiogroup" data-testid={`${testPrefix}-group`}>
    {options.map((option) => {
      const active = selected === option;
      const slug = option.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return (
        <label
          key={option}
          className={classNames(
            "flex items-center gap-3 rounded-2xl border px-4 py-3 transition-colors duration-200",
            active ? "border-[#4CAF50] bg-[#E8F5E9] text-[#2E7D32]" : "border-[#D1D5DB] bg-white text-slate-700 hover:border-[#4CAF50]",
          )}
          data-testid={`${testPrefix}-${slug}`}
        >
          <input
            type="radio"
            name={name}
            value={option}
            checked={active}
            onChange={() => onChange(option)}
            className="h-4 w-4 accent-[#4CAF50]"
            data-testid={`${testPrefix}-${slug}-input`}
          />
          <span className="text-sm font-medium">{option}</span>
        </label>
      );
    })}
  </div>
);

const EmptyState = ({ title, description, action }) => (
  <Card className="border-dashed border-[#D1D5DB] bg-[#FCFDFC] shadow-none" data-testid={`${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-empty-state`}>
    <CardContent className="flex flex-col gap-4 p-8">
      <div>
        <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 max-w-xl text-sm leading-7 text-slate-600">{description}</p>
      </div>
      {action}
    </CardContent>
  </Card>
);

const AuthShell = ({ title, description, children, footer, onLogoClick, logoButtonTestId = "brand-logo-button" }) => (
  <div className="min-h-screen auth-surface px-4 py-8">
    <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="rounded-[28px] border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur lg:p-10">
        <div className="mb-10 flex items-center gap-3" data-testid="brand-mark">
          <button type="button" onClick={onLogoClick} className="rounded-2xl" data-testid={logoButtonTestId}>
            <img src={LOGO_URL} alt="The Food Prescription logo" className="h-14 w-14 rounded-2xl border border-[#D1D5DB] bg-white object-contain p-1" data-testid="brand-logo-image" />
          </button>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4CAF50]">The Food Prescription</p>
            <p className="text-sm text-slate-500">Nutrition dashboard for guided care</p>
          </div>
        </div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600">{description}</p>
        <div className="mt-8">{children}</div>
        <div className="mt-8 text-sm text-slate-500">{footer}</div>
      </div>
      <div className="rounded-[28px] border border-[#D1D5DB] bg-white p-4 shadow-sm lg:p-6" data-testid="auth-image-panel">
        <div className="relative h-full overflow-hidden rounded-[22px] border border-[#D1D5DB]">
          <img src={HERO_IMAGE} alt="Healthy meals" className="h-full w-full object-cover" data-testid="auth-hero-image" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/75 to-transparent p-6 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-emerald-200">Professional care</p>
            <h2 className="mt-2 text-3xl font-semibold">Personalised meal plans that work for you and your whole family.</h2>
            <p className="mt-3 text-sm leading-7 text-slate-100">
              Simple, realistic meal plans that support your health and your family table.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PublicOnlyRoute = ({ children }) => {
  const { user, initializing } = useAuth();
  if (initializing) return <LoadingScreen label="Opening The Food Prescription..." />;
  if (user?.role === "admin") return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === "client") return <Navigate to="/client/home" replace />;
  return children;
};

const ProtectedRoute = ({ role, children }) => {
  const { user, initializing } = useAuth();
  if (initializing) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin/dashboard" : "/client/home"} replace />;
  }
  return children;
};

const LandingRedirect = () => {
  const { user, initializing } = useAuth();
  if (initializing) return <LoadingScreen label="Preparing your workspace..." />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin/dashboard" : "/client/home"} replace />;
};

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);

  const handleLogoClick = () => {
    setLogoClickCount((current) => Math.min(current + 1, 5));
  };

  const fillAdminCredentials = () => {
    setForm({ email: ADMIN_EMAIL, password: "Admin@123" });
    toast.success("Admin credentials filled.");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const user = await login(form);
      toast.success(`Welcome back, ${user.name}`);
      navigate(user.role === "admin" ? "/admin/dashboard" : "/client/home");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicOnlyRoute>
      <AuthShell
        title="Sign in to your nutrition workspace"
        description="Admins land in the full web dashboard. Clients reach their personal care portal with intake, meal logs, blood work and chat."
        onLogoClick={handleLogoClick}
        logoButtonTestId="login-brand-logo-button"
        footer={<div className="flex flex-wrap gap-4"><Link to="/signup" className="text-[#2E7D32] font-semibold" data-testid="login-signup-link">Client signup</Link><Link to="/forgot-password" className="text-slate-600 font-semibold" data-testid="login-forgot-password-link">Forgot password</Link></div>}
      >
        <Card className="border-[#D1D5DB] shadow-sm">
          <CardContent className="p-6">
            <form className="space-y-5" onSubmit={handleSubmit} data-testid="login-form">
              {logoClickCount >= 5 ? (
                <div className="rounded-2xl border border-[#D1D5DB] bg-[#F8FAF8] p-4" data-testid="login-admin-shortcut-panel">
                  <p className="text-sm text-slate-600">Admin shortcut unlocked.</p>
                  <Button type="button" variant="outline" className="mt-3 rounded-full border-[#D1D5DB]" onClick={fillAdminCredentials} data-testid="login-admin-autofill-button">
                    Auto-fill admin credentials
                  </Button>
                </div>
              ) : null}
              <FormField label="Email" testId="login-email-field">
                <Input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} data-testid="login-email-input" />
              </FormField>
              <FormField label="Password" testId="login-password-field">
                <Input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} data-testid="login-password-input" />
              </FormField>
              <Button type="submit" className="w-full bg-[#4CAF50] hover:bg-[#43A047]" disabled={loading} data-testid="login-submit-button">
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </AuthShell>
    </PublicOnlyRoute>
  );
};

const SignupPage = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Signup complete. The nutritionist will review your registration.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to sign up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicOnlyRoute>
      <AuthShell
        title="Create your client account"
        description="Use this route if you’re a client registering yourself. Your account stays pending until the nutritionist approves it."
        footer={<Link to="/login" className="font-semibold text-[#2E7D32]" data-testid="signup-login-link">Back to login</Link>}
      >
        <Card className="border-[#D1D5DB] shadow-sm">
          <CardContent className="p-6">
            <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit} data-testid="signup-form">
              <FormField label="Full name" testId="signup-name-field"><Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} data-testid="signup-name-input" /></FormField>
              <FormField label="Phone" testId="signup-phone-field"><Input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} data-testid="signup-phone-input" /></FormField>
              <FormField label="Email" testId="signup-email-field"><Input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} data-testid="signup-email-input" /></FormField>
              <FormField label="Password" testId="signup-password-field"><Input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} data-testid="signup-password-input" /></FormField>
              <div className="md:col-span-2"><Button type="submit" className="w-full bg-[#4CAF50] hover:bg-[#43A047]" disabled={loading} data-testid="signup-submit-button">{loading ? "Creating account..." : "Create account"}</Button></div>
            </form>
          </CardContent>
        </Card>
      </AuthShell>
    </PublicOnlyRoute>
  );
};

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [previewLink, setPreviewLink] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await forgotPassword({ email });
      setPreviewLink(response.reset_link_preview || "");
      toast.success(response.message);
    } catch {
      toast.error("Unable to prepare a reset link right now");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicOnlyRoute>
      <AuthShell title="Forgot your password?" description="Request a secure reset link. If email delivery isn’t configured yet, you’ll still see a preview link for testing." footer={<Link to="/login" className="font-semibold text-[#2E7D32]" data-testid="forgot-password-login-link">Back to login</Link>}>
        <Card className="border-[#D1D5DB] shadow-sm">
          <CardContent className="space-y-6 p-6">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <FormField label="Email" testId="forgot-password-email-field"><Input value={email} onChange={(event) => setEmail(event.target.value)} data-testid="forgot-password-email-input" /></FormField>
              <Button type="submit" className="w-full bg-[#4CAF50] hover:bg-[#43A047]" disabled={loading} data-testid="forgot-password-submit-button">{loading ? "Preparing link..." : "Send reset link"}</Button>
            </form>
            {previewLink ? <div className="rounded-2xl border border-[#D1D5DB] bg-[#F8FAF8] p-4 text-sm text-slate-700" data-testid="forgot-password-preview-link">Preview reset link: <a href={previewLink} className="font-semibold text-[#2E7D32]">{previewLink}</a></div> : null}
          </CardContent>
        </Card>
      </AuthShell>
    </PublicOnlyRoute>
  );
};

const ResetPasswordPage = () => {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const token = new URLSearchParams(location.search).get("token") || "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await resetPassword({ token, new_password: password });
      toast.success("Password updated. You can sign in now.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset link is invalid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicOnlyRoute>
      <AuthShell title="Set a new password" description="Use the secure link from your email to update your password." footer={<Link to="/login" className="font-semibold text-[#2E7D32]" data-testid="reset-password-login-link">Back to login</Link>}>
        <Card className="border-[#D1D5DB] shadow-sm">
          <CardContent className="p-6">
            <form className="space-y-5" onSubmit={handleSubmit} data-testid="reset-password-form">
              <FormField label="New password" testId="reset-password-field"><Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} data-testid="reset-password-input" /></FormField>
              <Button type="submit" className="w-full bg-[#4CAF50] hover:bg-[#43A047]" disabled={loading || !token} data-testid="reset-password-submit-button">{loading ? "Saving..." : "Update password"}</Button>
            </form>
          </CardContent>
        </Card>
      </AuthShell>
    </PublicOnlyRoute>
  );
};

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = [
    { icon: LayoutDashboard, label: "Dashboard", to: "/admin/dashboard", testId: "admin-sidebar-dashboard-link" },
    { icon: Users, label: "Clients", to: "/admin/clients", testId: "admin-sidebar-clients-link" },
    { icon: Bell, label: "Nudge", to: "/admin/nudge", testId: "admin-sidebar-nudge-link" },
    { icon: MessageSquare, label: "Chat", to: "/admin/chat", testId: "admin-sidebar-chat-link" },
  ];
  return (
    <div className="min-h-screen bg-[#F8FAF8] md:grid md:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="border-r border-[#D1D5DB] bg-white p-6" data-testid="admin-sidebar">
        <div className="rounded-3xl border border-[#D1D5DB] bg-[#F8FAF8] p-5">
          <img src={LOGO_URL} alt="The Food Prescription logo" className="h-20 w-20 rounded-3xl border border-[#D1D5DB] bg-white object-contain p-1" data-testid="admin-sidebar-logo-image" />
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.24em] text-[#4CAF50]">The Food Prescription</p>
          <h2 className="mt-3 text-2xl font-bold text-slate-900">Nutritionist Console</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600" data-testid="admin-sidebar-user-name">{user?.name}</p>
        </div>
        <nav className="mt-8 grid gap-2" data-testid="admin-sidebar-navigation">
          {links.map(({ icon: Icon, label, to, testId }) => (
            <NavLink key={to} to={to} data-testid={testId} className={({ isActive }) => classNames("flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors duration-200", isActive ? "border-[#4CAF50] bg-[#E8F5E9] text-[#2E7D32]" : "border-transparent text-slate-600 hover:border-[#D1D5DB] hover:bg-slate-50") }>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <Button variant="outline" className="mt-8 w-full justify-start rounded-2xl border-[#D1D5DB]" onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout-button"><LogOut className="mr-2 h-4 w-4" />Logout</Button>
      </aside>
      <main className="p-4 md:p-8"><div className="mx-auto max-w-7xl space-y-8">{children}</div></main>
    </div>
  );
};

const ClientLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = [
    { icon: Home, label: "Home", to: "/client/home", testId: "client-nav-home-link" },
    { icon: ClipboardList, label: "Intake Form", to: "/client/intake-form", testId: "client-nav-intake-link" },
    { icon: Salad, label: "Meal Log", to: "/client/meal-log", testId: "client-nav-meal-log-link" },
    { icon: TestTube2, label: "Blood Work", to: "/client/blood-work", testId: "client-nav-blood-work-link" },
    { icon: MessageSquare, label: "Chat", to: "/client/chat", testId: "client-nav-chat-link" },
    { icon: User, label: "Profile", to: "/client/profile", testId: "client-nav-profile-link" },
  ];
  return (
    <div className="min-h-screen bg-[#F8FAF8]" data-testid="client-layout">
      <header className="border-b border-[#D1D5DB] bg-white/90 px-4 py-4 backdrop-blur md:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="The Food Prescription logo" className="h-14 w-14 rounded-2xl border border-[#D1D5DB] bg-white object-contain p-1" data-testid="client-header-logo-image" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4CAF50]">The Food Prescription</p>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="client-header-name">Welcome, {user?.name}</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {links.map(({ icon: Icon, label, to, testId }) => (
              <NavLink key={to} to={to} data-testid={testId} className={({ isActive }) => classNames("inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200", isActive ? "border-[#4CAF50] bg-[#E8F5E9] text-[#2E7D32]" : "border-[#D1D5DB] bg-white text-slate-600 hover:border-[#4CAF50]") }>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            <Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => { logout(); navigate("/login"); }} data-testid="client-logout-button"><LogOut className="mr-2 h-4 w-4" />Logout</Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-8 px-4 py-6 md:px-8 md:py-8">{children}</main>
    </div>
  );
};

const AdminDashboardPage = () => {
  const { authRequest } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const response = await authRequest("get", "/admin/dashboard");
    setData(response);
  }, [authRequest]);

  useEffect(() => { load().catch(() => toast.error("Unable to load dashboard")); }, [load]);
  if (!data) return <LoadingScreen label="Loading dashboard insights..." />;

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin dashboard" title="Your nutrition operations at a glance" description="Track client approvals, plan generation activity and follow-up actions from one clean workspace." />
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Total active clients" value={data.stats.total_active_clients} subtext="Approved clients currently in care" testId="admin-stat-total-active-clients" onClick={() => navigate("/admin/clients?status=active")} />
        <MetricCard label="Pending approvals" value={data.stats.pending_approvals} subtext="New signups waiting for review" testId="admin-stat-pending-approvals" onClick={() => navigate("/admin/clients?status=pending")} />
        <MetricCard label="Plans this month" value={data.stats.meal_plans_generated_this_month} subtext="Fresh meal plans created recently" testId="admin-stat-meal-plans-month" />
        <MetricCard label="Activity feed" value={data.recent_activity.length} subtext="Recent actions from your workspace" testId="admin-stat-recent-activity" />
      </div>
      <Card className="border-[#D1D5DB] shadow-sm" data-testid="admin-recent-activity-card">
        <CardHeader><CardTitle className="text-xl text-slate-900">Recent activity</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {data.recent_activity.map((item, index) => (
            <div key={item.activity_id || index} className="rounded-2xl border border-[#D1D5DB] bg-[#FCFDFC] p-4" data-testid={`admin-activity-item-${index}`}>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{item.title}</p>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{friendlyDate(item.created_at)}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const ClientsPage = () => {
  const { authRequest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const statusFilter = new URLSearchParams(location.search).get("status") || "";

  const loadClients = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    const query = params.toString();
    const response = await authRequest("get", `/admin/clients${query ? `?${query}` : ""}`);
    setClients(response.clients || []);
  }, [authRequest, search, statusFilter]);

  useEffect(() => { loadClients().catch(() => toast.error("Unable to load clients")); }, [loadClients]);

  const createClient = async (event) => {
    event.preventDefault();
    try {
      await authRequest("post", "/admin/clients", form);
      toast.success("Client created successfully");
      setForm({ name: "", email: "", phone: "", password: "" });
      setShowCreate(false);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create client");
    }
  };

  const updateStatus = async (clientId, status) => {
    await authRequest("patch", `/admin/clients/${clientId}/status`, { status });
    toast.success(`Client marked ${status}`);
    loadClients();
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm("Delete this client and all related records?")) return;
    await authRequest("delete", `/admin/clients/${clientId}`);
    toast.success("Client deleted");
    loadClients();
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Client management"
        title="Clients and approvals"
        description={`Search active and pending clients, create new accounts instantly and jump into each care record.${statusFilter ? ` Currently showing ${statusFilter} clients only.` : ""}`}
        actions={<><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={loadClients} data-testid="clients-refresh-button">Refresh</Button><Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => setShowCreate((current) => !current)} data-testid="clients-toggle-create-button">{showCreate ? "Close form" : "Create client"}</Button></>}
      />
      <Card className="border-[#D1D5DB] shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input className="pl-10" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name, email or phone" data-testid="clients-search-input" />
            </div>
            {statusFilter ? <Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => navigate("/admin/clients")} data-testid="clients-clear-filter-button">Clear {statusFilter} filter</Button> : null}
          </div>
          {showCreate ? (
            <form className="mt-6 grid gap-4 rounded-3xl border border-[#D1D5DB] bg-[#F8FAF8] p-5 md:grid-cols-2" onSubmit={createClient} data-testid="create-client-form">
              <FormField label="Full name" testId="create-client-name-field"><Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} data-testid="create-client-name-input" /></FormField>
              <FormField label="Phone" testId="create-client-phone-field"><Input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} data-testid="create-client-phone-input" /></FormField>
              <FormField label="Email" testId="create-client-email-field"><Input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} data-testid="create-client-email-input" /></FormField>
              <FormField label="Password" testId="create-client-password-field"><Input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} data-testid="create-client-password-input" /></FormField>
              <div className="md:col-span-2"><Button type="submit" className="bg-[#4CAF50] hover:bg-[#43A047]" data-testid="create-client-submit-button">Create client</Button></div>
            </form>
          ) : null}
          <div className="mt-6 overflow-hidden rounded-3xl border border-[#D1D5DB]" data-testid="clients-table-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Intake</TableHead>
                  <TableHead>Approved plan</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.user_id} data-testid={`client-row-${client.user_id}`}>
                    <TableCell className="font-semibold text-slate-900">{client.name}</TableCell>
                    <TableCell className="max-w-[220px] break-all text-sm text-slate-600" data-testid={`client-email-${client.user_id}`}>{client.email}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600" data-testid={`client-phone-${client.user_id}`}>{client.phone || "—"}</TableCell>
                    <TableCell><StatusPill value={client.status} testId={`client-status-${client.user_id}`} /></TableCell>
                    <TableCell data-testid={`client-intake-${client.user_id}`}>{client.has_intake_form ? "Submitted" : "Pending"}</TableCell>
                    <TableCell data-testid={`client-approved-plan-${client.user_id}`}>{client.has_approved_plan ? "Yes" : "No"}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600" data-testid={`client-joined-${client.user_id}`}>{friendlyDate(client.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => navigate(`/admin/clients/${client.user_id}`)} data-testid={`view-client-${client.user_id}`}>View</Button>
                        {client.status !== "active" ? <Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => updateStatus(client.user_id, "active")} data-testid={`approve-client-${client.user_id}`}>Approve</Button> : null}
                        <Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => updateStatus(client.user_id, client.status === "inactive" ? "active" : "inactive")} data-testid={`toggle-client-${client.user_id}`}>{client.status === "inactive" ? "Activate" : "Deactivate"}</Button>
                        <Button variant="outline" className="rounded-full border-red-200 text-red-600" onClick={() => deleteClient(client.user_id)} data-testid={`delete-client-${client.user_id}`}>Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const IntakeOverview = ({ intakeForm }) => {
  if (!intakeForm?.data) return <EmptyState title="No intake form yet" description="This client has not submitted the intake form yet." />;
  const entries = Object.entries(intakeForm.data);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {entries.map(([key, value]) => (
        <Card key={key} className="border-[#D1D5DB] shadow-sm" data-testid={`intake-field-${key}`}>
          <CardContent className="p-5">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{key.replace(/_/g, " ")}</p>
            <p className="mt-2 text-sm leading-7 text-slate-700">{Array.isArray(value) ? value.join(", ") : String(value || "—")}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const ChatPanel = ({ messages, currentUserId, onSend, sending, placeholder }) => {
  const [content, setContent] = useState("");
  return (
    <Card className="border-[#D1D5DB] shadow-sm" data-testid="chat-panel">
      <CardContent className="space-y-4 p-6">
        <div className="max-h-[420px] space-y-3 overflow-y-auto rounded-3xl border border-[#D1D5DB] bg-[#FCFDFC] p-4" data-testid="chat-message-list">
          {messages.length ? messages.map((message, index) => {
            const isCurrentUser = message.sender_id === currentUserId;
            return (
              <div key={message.message_id || index} className={classNames("flex", isCurrentUser ? "justify-end" : "justify-start")} data-testid={`chat-message-${index}`}>
                <div className={classNames("max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-7", isCurrentUser ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-slate-100 text-slate-700")}>
                  <p>{message.content}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.16em] opacity-70">{friendlyDate(message.timestamp)}</p>
                </div>
              </div>
            );
          }) : <p className="text-sm text-slate-500" data-testid="chat-empty-message">No messages yet.</p>}
        </div>
        <form className="flex flex-col gap-3 md:flex-row" onSubmit={(event) => { event.preventDefault(); if (!content.trim()) return; onSend(content).then(() => setContent("")); }} data-testid="chat-send-form">
          <Textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder={placeholder} className="min-h-[56px] flex-1" data-testid="chat-message-input" />
          <Button type="submit" className="rounded-2xl bg-[#4CAF50] hover:bg-[#43A047]" disabled={sending || !content.trim()} data-testid="chat-send-button">{sending ? "Sending..." : "Send"}</Button>
        </form>
      </CardContent>
    </Card>
  );
};

const EditableMealCard = ({ meal, editable, onSave, onRecalculate, index, dayIndex }) => {
  const [draft, setDraft] = useState(meal);
  useEffect(() => { setDraft(meal); }, [meal]);
  return (
    <div className="rounded-3xl border border-[#D1D5DB] bg-white p-4" data-testid={`meal-card-${dayIndex}-${index}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{meal.meal_label}</p>
          <StatusPill value={`${Math.round(Number(draft.calories || 0))} kcal`} testId={`meal-card-calories-${dayIndex}-${index}`} />
        </div>
      </div>
      {editable ? (
        <div className="grid gap-3">
          <Input value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} data-testid={`meal-title-input-${dayIndex}-${index}`} />
          <Textarea value={draft.details} onChange={(event) => setDraft((current) => ({ ...current, details: event.target.value }))} data-testid={`meal-details-input-${dayIndex}-${index}`} />
          <div className="grid gap-3 md:grid-cols-4">
            <Input value={draft.calories} onChange={(event) => setDraft((current) => ({ ...current, calories: event.target.value }))} data-testid={`meal-calories-input-${dayIndex}-${index}`} />
            <Input value={draft.protein} onChange={(event) => setDraft((current) => ({ ...current, protein: event.target.value }))} data-testid={`meal-protein-input-${dayIndex}-${index}`} />
            <Input value={draft.carbs} onChange={(event) => setDraft((current) => ({ ...current, carbs: event.target.value }))} data-testid={`meal-carbs-input-${dayIndex}-${index}`} />
            <Input value={draft.fat} onChange={(event) => setDraft((current) => ({ ...current, fat: event.target.value }))} data-testid={`meal-fat-input-${dayIndex}-${index}`} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button className="w-fit rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => onSave(draft)} data-testid={`meal-save-button-${dayIndex}-${index}`}>Save meal</Button>
            <Button variant="outline" className="w-fit rounded-full border-[#D1D5DB]" onClick={() => onRecalculate(draft, setDraft)} data-testid={`meal-recalculate-button-${dayIndex}-${index}`}>Recalculate macros</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <h4 className="text-lg font-semibold text-slate-900">{meal.title}</h4>
          <p className="text-sm leading-7 text-slate-600">{meal.details}</p>
          <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
            <Badge variant="outline">Protein {meal.protein}g</Badge>
            <Badge variant="outline">Carbs {meal.carbs}g</Badge>
            <Badge variant="outline">Fat {meal.fat}g</Badge>
          </div>
        </div>
      )}
    </div>
  );
};

const MealPlanViewer = ({ mealPlan, editable, onSaveMeal, onRecalculateMeal, onApprove }) => {
  if (!mealPlan) {
    return <EmptyState title="No meal plan yet" description="Generate the first 7-day plan after the client completes the intake form." />;
  }
  return (
    <div className="space-y-6" data-testid="meal-plan-viewer">
      <Card className="border-[#D1D5DB] shadow-sm">
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4CAF50]">Meal plan summary</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-900" data-testid="meal-plan-calorie-target">{mealPlan.calorie_target} kcal target</h3>
              <p className="mt-2 text-sm leading-7 text-slate-600" data-testid="meal-plan-reasoning">{mealPlan.ai_reasoning}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill value={mealPlan.status} testId="meal-plan-status-pill" />
              {mealPlan.excel_file_url ? <a href={mealPlan.excel_file_url} download={mealPlan.excel_file_name || "meal-plan.xlsx"} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid="meal-plan-download-excel-button">Download Excel</a> : null}
              {editable && mealPlan.status !== "approved" ? <Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={onApprove} data-testid="meal-plan-approve-button">Approve plan</Button> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-slate-700">
            <Badge variant="outline" data-testid="meal-plan-protein-badge">Protein {mealPlan.macros?.protein}g</Badge>
            <Badge variant="outline" data-testid="meal-plan-carbs-badge">Carbs {mealPlan.macros?.carbs}g</Badge>
            <Badge variant="outline" data-testid="meal-plan-fat-badge">Fat {mealPlan.macros?.fat}g</Badge>
          </div>
        </CardContent>
      </Card>
      {(mealPlan.structured_plan || []).map((day, dayIndex) => (
        <Card key={day.day} className="border-[#D1D5DB] shadow-sm" data-testid={`meal-plan-day-${day.day}`}>
          <CardHeader>
            <CardTitle className="flex flex-col gap-2 text-xl text-slate-900 md:flex-row md:items-center md:justify-between">
              <span>Day {day.day}: {day.theme}</span>
              <span className="text-sm font-semibold text-slate-500">{day.totals?.calories} kcal · {day.totals?.water_litres || 0}L water</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {(day.meals || []).map((meal, mealIndex) => (
              <EditableMealCard key={`${day.day}-${mealIndex}`} meal={meal} editable={editable} dayIndex={dayIndex} index={mealIndex} onSave={(draft) => onSaveMeal(dayIndex, mealIndex, draft)} onRecalculate={(draft, setDraft) => onRecalculateMeal(dayIndex, mealIndex, draft, setDraft)} />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const { authRequest, user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [planNotes, setPlanNotes] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [sending, setSending] = useState(false);

  const loadDetail = useCallback(async () => {
    const response = await authRequest("get", `/admin/clients/${id}`);
    setDetail(response);
  }, [authRequest, id]);

  useEffect(() => { loadDetail().catch(() => toast.error("Unable to load client record")); }, [loadDetail]);
  if (!detail) return <LoadingScreen label="Opening client record..." />;

  const setStatus = async (status) => {
    await authRequest("patch", `/admin/clients/${id}/status`, { status });
    toast.success(`Client marked ${status}`);
    loadDetail();
  };

  const runPasswordReset = async () => {
    await authRequest("post", `/admin/clients/${id}/reset-password`, { new_password: resetPassword });
    toast.success("Client password updated");
    setResetPassword("");
  };

  const generatePlan = async () => {
    await authRequest("post", `/admin/clients/${id}/meal-plan/generate`, { notes: planNotes });
    toast.success("Meal plan draft generated");
    loadDetail();
  };

  const approvePlan = async () => {
    await authRequest("post", `/admin/meal-plans/${detail.meal_plan.plan_id}/approve`);
    toast.success("Meal plan approved");
    loadDetail();
  };

  const saveMeal = async (dayIndex, mealIndex, draft) => {
    await authRequest("put", `/admin/meal-plans/${detail.meal_plan.plan_id}`, {
      day_index: dayIndex,
      meal_index: mealIndex,
      title: draft.title,
      details: draft.details,
      calories: Number(draft.calories),
      protein: Number(draft.protein),
      carbs: Number(draft.carbs),
      fat: Number(draft.fat),
    });
    toast.success("Meal updated");
    loadDetail();
  };

  const recalculateMeal = async (dayIndex, mealIndex, draft, setDraft) => {
    const response = await authRequest("post", `/admin/meal-plans/${detail.meal_plan.plan_id}/recalculate-meal`, {
      day_index: dayIndex,
      meal_index: mealIndex,
      title: draft.title,
      details: draft.details,
    });
    const updatedMeal = response.meal;
    setDraft((current) => ({ ...current, ...updatedMeal }));
    toast.success("Meal calories and macros recalculated");
    loadDetail();
  };

  const uploadExcel = async () => {
    if (!detail?.meal_plan?.plan_id || !excelFile) return toast.error("Select an Excel file first");
    const formData = new FormData();
    formData.append("file", excelFile);
    await authRequest("post", `/admin/meal-plans/${detail.meal_plan.plan_id}/excel`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    setExcelFile(null);
    toast.success("Meal plan Excel uploaded");
    loadDetail();
  };

  const sendMessage = async (content) => {
    setSending(true);
    try {
      await authRequest("post", `/admin/clients/${id}/messages`, { content });
      await loadDetail();
    } finally {
      setSending(false);
    }
  };

  const reanalyzeBloodWork = async (bloodWorkId) => {
    await authRequest("post", `/admin/blood-work/${bloodWorkId}/reanalyze`);
    toast.success("Blood work re-analyzed");
    loadDetail();
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Client detail"
        title={detail.client.name}
        description="Review intake, uploads, chat and the AI-assisted meal plan in a single record."
        actions={<><StatusPill value={detail.client.status} testId="client-detail-status" /><Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => setStatus("active")} data-testid="client-detail-approve-button">Approve</Button><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => setStatus(detail.client.status === "inactive" ? "active" : "inactive")} data-testid="client-detail-toggle-status-button">{detail.client.status === "inactive" ? "Activate" : "Deactivate"}</Button></>}
      />
      <Card className="border-[#D1D5DB] shadow-sm" data-testid="client-detail-overview-card">
        <CardContent className="grid gap-4 p-6 md:grid-cols-4">
          <div data-testid="client-detail-email"><p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Email</p><p className="mt-2 font-semibold text-slate-900">{detail.client.email}</p></div>
          <div data-testid="client-detail-phone"><p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Phone</p><p className="mt-2 font-semibold text-slate-900">{detail.client.phone || "—"}</p></div>
          <div data-testid="client-detail-joined"><p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Joined</p><p className="mt-2 font-semibold text-slate-900">{friendlyDate(detail.client.created_at)}</p></div>
          <div className="space-y-3" data-testid="client-detail-manual-reset-card">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Manual reset</p>
            <Input value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} placeholder="New password" data-testid="client-detail-manual-reset-input" />
            <Button className="w-full rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={runPasswordReset} disabled={!resetPassword} data-testid="client-detail-manual-reset-button">Reset password</Button>
          </div>
        </CardContent>
      </Card>
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex h-auto flex-wrap gap-2 rounded-2xl bg-transparent p-0">
          {[
            ["overview", "Overview"],
            ["intake", "Intake Form"],
            ["blood-work", "Blood Work"],
            ["meal-photos", "Meal Photos"],
            ["meal-plan", "AI Meal Plan"],
            ["chat", "Chat"],
          ].map(([value, label]) => <TabsTrigger key={value} value={value} className="rounded-full border border-[#D1D5DB] bg-white px-4 py-2 data-[state=active]:border-[#4CAF50] data-[state=active]:bg-[#E8F5E9] data-[state=active]:text-[#2E7D32]" data-testid={`client-detail-tab-${value}`}>{label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Card className="border-[#D1D5DB] shadow-sm"><CardContent className="grid gap-4 p-6 md:grid-cols-3"><MetricCard label="Blood reports" value={detail.blood_works.length} subtext="Uploaded reports" testId="client-detail-blood-work-count" /><MetricCard label="Meal photos" value={detail.meal_logs.length} subtext="Captured meals" testId="client-detail-meal-log-count" /><MetricCard label="Chat messages" value={detail.messages.length} subtext="Conversation history" testId="client-detail-message-count" /></CardContent></Card></TabsContent>
        <TabsContent value="intake"><IntakeOverview intakeForm={detail.intake_form} /></TabsContent>
        <TabsContent value="blood-work"><div className="space-y-4">{detail.blood_works.length ? detail.blood_works.map((item) => <Card key={item.blood_work_id} className="border-[#D1D5DB] shadow-sm" data-testid={`blood-work-card-${item.blood_work_id}`}><CardContent className="space-y-4 p-6"><div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><div><p className="text-lg font-semibold text-slate-900">{item.file_name}</p><p className="text-sm text-slate-600">{friendlyDate(item.created_at)}</p></div><div className="flex flex-wrap gap-3"><a href={item.file_url} download={item.file_name || "blood-report"} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`blood-work-download-${item.blood_work_id}`}>Download report</a><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => reanalyzeBloodWork(item.blood_work_id)} data-testid={`blood-work-reanalyze-${item.blood_work_id}`}>Re-analyze</Button></div></div><p className="text-sm leading-7 text-slate-600">{item.ai_analysis?.ai_summary || "No summary available."}</p><div className="flex flex-wrap gap-2">{Object.entries(item.extracted_values || {}).map(([key, value]) => <Badge key={key} variant="outline" data-testid={`blood-work-value-${item.blood_work_id}-${key}`}>{key}: {value}</Badge>)}</div></CardContent></Card>) : <EmptyState title="No blood work uploaded" description="Once the client uploads reports, extracted values and the health summary will appear here." />}</div></TabsContent>
        <TabsContent value="meal-photos"><div className="grid gap-4 lg:grid-cols-2">{detail.meal_logs.length ? detail.meal_logs.map((item) => <Card key={item.meal_log_id} className="border-[#D1D5DB] shadow-sm" data-testid={`meal-log-card-${item.meal_log_id}`}><CardContent className="space-y-4 p-6">{item.image_url ? <div className="overflow-hidden rounded-3xl border border-[#D1D5DB] bg-[#F8FAF8] p-3"><img src={item.image_url} alt={item.meal_type} className="h-64 w-full object-contain" data-testid={`meal-log-image-${item.meal_log_id}`} /></div> : null}<div className="flex items-center justify-between"><p className="text-lg font-semibold text-slate-900">{item.meal_type}</p><p className="text-sm text-slate-500">{friendlyDate(item.created_at)}</p></div><p className="text-sm leading-7 text-slate-600">{item.ai_analysis?.summary}</p><div className="flex flex-wrap gap-2"><Badge variant="outline">{item.ai_analysis?.calories} kcal</Badge><Badge variant="outline">Protein {item.ai_analysis?.protein}g</Badge><Badge variant="outline">Carbs {item.ai_analysis?.carbs}g</Badge><Badge variant="outline">Fat {item.ai_analysis?.fat}g</Badge></div><div className="flex flex-wrap gap-3"><a href={item.image_url} download={`${item.meal_type || 'meal-photo'}.png`} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`meal-log-download-${item.meal_log_id}`}>Download photo</a><a href={item.image_url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`meal-log-open-${item.meal_log_id}`}>Open photo</a></div></CardContent></Card>) : <EmptyState title="No meal photos yet" description="Meal uploads will show calories, macros and estimated portion details here." />}</div></TabsContent>
        <TabsContent value="meal-plan"><div className="space-y-6"><Card className="border-[#D1D5DB] shadow-sm"><CardContent className="space-y-4 p-6"><Textarea value={planNotes} onChange={(event) => setPlanNotes(event.target.value)} placeholder="Add context for generation, such as scheduling preferences or notes from your review." data-testid="meal-plan-notes-input" /><div className="flex flex-wrap gap-3"><Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={generatePlan} data-testid="generate-meal-plan-button">Generate meal plan</Button>{detail.meal_plan ? <><input type="file" accept=".xlsx,.xls,.csv" onChange={(event) => setExcelFile(event.target.files?.[0] || null)} className="rounded-2xl border border-dashed border-[#D1D5DB] bg-[#F8FAF8] px-4 py-2 text-sm" data-testid="meal-plan-excel-file-input" /><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={uploadExcel} data-testid="meal-plan-upload-excel-button">Upload Excel</Button></> : null}</div>{detail.meal_plan?.excel_file_url ? <a href={detail.meal_plan.excel_file_url} download={detail.meal_plan.excel_file_name || "meal-plan.xlsx"} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid="meal-plan-current-excel-link">Download current Excel</a> : null}</CardContent></Card><MealPlanViewer mealPlan={detail.meal_plan} editable onSaveMeal={saveMeal} onRecalculateMeal={recalculateMeal} onApprove={approvePlan} /></div></TabsContent>
        <TabsContent value="chat"><ChatPanel messages={detail.messages || []} currentUserId={user.user_id} onSend={sendMessage} sending={sending} placeholder="Send a message to the client" /></TabsContent>
      </Tabs>
    </div>
  );
};

const AdminChatPage = () => {
  const { authRequest, user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);

  const loadOverview = useCallback(async () => {
    const response = await authRequest("get", "/admin/chats");
    setConversations(response.conversations || []);
    if (!selectedId && response.conversations?.[0]?.client?.user_id) {
      setSelectedId(response.conversations[0].client.user_id);
    }
  }, [authRequest, selectedId]);

  const loadMessages = useCallback(async () => {
    if (!selectedId) return;
    const response = await authRequest("get", `/admin/clients/${selectedId}/messages`);
    setMessages(response.messages || []);
  }, [authRequest, selectedId]);

  useEffect(() => { loadOverview().catch(() => toast.error("Unable to load conversations")); }, [loadOverview]);
  useEffect(() => { loadMessages().catch(() => toast.error("Unable to load chat")); }, [loadMessages]);

  const sendMessage = async (content) => {
    setSending(true);
    try {
      await authRequest("post", `/admin/clients/${selectedId}/messages`, { content });
      await loadMessages();
      await loadOverview();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin chat" title="Conversations with clients" description="Stay on top of messages sorted by most recent activity." />
      <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
        <Card className="border-[#D1D5DB] shadow-sm" data-testid="admin-chat-conversation-list"><CardContent className="space-y-3 p-4">{conversations.map((conversation, index) => <button key={conversation.client.user_id} type="button" onClick={() => setSelectedId(conversation.client.user_id)} className={classNames("w-full rounded-3xl border p-4 text-left transition-colors duration-200", selectedId === conversation.client.user_id ? "border-[#4CAF50] bg-[#E8F5E9]" : "border-[#D1D5DB] bg-white hover:border-[#4CAF50]")} data-testid={`admin-chat-conversation-${index}`}><p className="font-semibold text-slate-900">{conversation.client.name}</p><p className="mt-1 text-sm text-slate-600">{conversation.last_message?.content || "No messages yet"}</p></button>)}</CardContent></Card>
        <div>{selectedId ? <ChatPanel messages={messages} currentUserId={user.user_id} onSend={sendMessage} sending={sending} placeholder="Send a message from the admin dashboard" /> : <EmptyState title="Select a conversation" description="Pick a client from the list to view and send messages." action={<Button className="w-fit rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => conversations[0] && setSelectedId(conversations[0].client.user_id)} data-testid="admin-chat-select-first-button">Open latest conversation</Button>} />}</div>
      </div>
      <Card className="border-[#D1D5DB] shadow-sm"><CardContent className="p-6"><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => selectedId && navigate(`/admin/clients/${selectedId}`)} data-testid="admin-chat-open-client-record-button">Open selected client record</Button></CardContent></Card>
    </div>
  );
};

const AdminNudgePage = () => {
  const { authRequest } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const response = await authRequest("get", "/admin/nudges");
    setItems(response.nudges || []);
  }, [authRequest]);

  useEffect(() => { load().catch(() => toast.error("Unable to load nudges")); }, [load]);

  const sendReminder = async (clientId, content) => {
    await authRequest("post", `/admin/nudges/${clientId}/send`, { content });
    toast.success("Reminder sent");
    load();
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Nudge center" title="Follow-up reminders" description="Prompt clients who still need to submit intake details or get their first meal plan generated." />
      {items.length ? <div className="grid gap-4">{items.map((item, index) => <Card key={item.client.user_id} className="border-[#D1D5DB] shadow-sm" data-testid={`nudge-card-${index}`}><CardContent className="flex flex-col gap-4 p-6 xl:flex-row xl:items-center xl:justify-between"><div><p className="text-lg font-semibold text-slate-900">{item.client.name}</p><p className="mt-2 text-sm text-slate-600">{item.reasons.join(" · ")}</p><p className="mt-2 text-sm text-slate-500">Suggested: {item.suggested_message}</p></div><Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => sendReminder(item.client.user_id, item.suggested_message)} data-testid={`send-nudge-button-${item.client.user_id}`}>Send reminder</Button></CardContent></Card>)}</div> : <EmptyState title="No nudges needed" description="Every client currently has an intake form and at least one meal plan record." />}
    </div>
  );
};

const ClientHomePage = () => {
  const { authRequest } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(async () => { setData(await authRequest("get", "/client/home")); }, [authRequest]);
  useEffect(() => { load().catch(() => toast.error("Unable to load home")); }, [load]);
  useEffect(() => {
    if (data && !data.intake_form_submitted) {
      navigate("/client/intake-form", { replace: true });
    }
  }, [data, navigate]);
  if (!data) return <LoadingScreen label="Loading your care plan..." />;
  if (!data.intake_form_submitted) return <LoadingScreen label="Redirecting you to complete your intake form..." />;

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Client home" title="Your care snapshot" description="See your current plan status, latest uploads and next steps from one clean home view." />
      <div className="grid gap-6 md:grid-cols-3">
        <MetricCard label="Intake form" value={data.intake_form_submitted ? "Done" : "Pending"} subtext="Complete this to unlock plan generation" testId="client-home-intake-status" />
        <MetricCard label="Approved meal plan" value={data.approved_meal_plan ? "Ready" : "Pending"} subtext="Your nutritionist approves plans before they appear here" testId="client-home-meal-plan-status" />
        <MetricCard label="Latest blood work" value={data.latest_blood_work ? "Uploaded" : "Not yet"} subtext="Upload reports to improve your plan accuracy" testId="client-home-blood-work-status" />
      </div>
      {data.approved_meal_plan ? <MealPlanViewer mealPlan={data.approved_meal_plan} editable={false} onSaveMeal={() => {}} onRecalculateMeal={() => {}} /> : <EmptyState title="Meal plan coming soon" description="Your approved 7-day plan will appear here as soon as the nutritionist reviews and approves it." />}
    </div>
  );
};

const ClientIntakeFormPage = () => {
  const { authRequest } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    age: "",
    gender: "Female",
    weight: "",
    height: "",
    occupation: "",
    health_goals: [],
    health_conditions: "",
    current_medications: "",
    food_allergies: "",
    food_dislikes: "",
    diet_type: "Vegetarian",
    activity_level: "Sedentary",
    morning_beverage_preference: "Tea with skimmed milk + stevia",
    meals_skipped: "Never",
    uric_acid: "",
    hba1c: "",
    fasting_blood_sugar: "",
    tsh: "",
    meal_timing_preference: "Regular",
    water_intake: "2.5",
  });
  const steps = ["Personal", "Goals", "Medical", "Lifestyle"];

  useEffect(() => {
    authRequest("get", "/client/intake-form")
      .then((response) => response.intake_form?.data && setForm((current) => ({ ...current, ...response.intake_form.data })))
      .catch(() => toast.error("Unable to load intake form"));
  }, [authRequest]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const toggleGoal = (value) => setForm((current) => ({ ...current, health_goals: current.health_goals.includes(value) ? current.health_goals.filter((goal) => goal !== value) : [...current.health_goals, value] }));
  const submit = async () => {
    setLoading(true);
    try {
      await authRequest("post", "/client/intake-form", { data: form });
      toast.success("Intake form saved");
    } catch {
      toast.error("Unable to save intake form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Intake form" title="Tell us about your health and routine" description="This multi-step form helps build better meal plans, nudges and analysis." />
      <Card className="border-[#D1D5DB] shadow-sm" data-testid="client-intake-form-card">
        <CardContent className="space-y-6 p-6 md:p-8">
          <div className="space-y-3"><div className="flex items-center justify-between"><p className="text-sm font-semibold text-slate-700">Progress</p><p className="text-sm text-slate-500" data-testid="intake-form-step-indicator">Step {step + 1} of {steps.length}</p></div><div className="h-3 overflow-hidden rounded-full bg-[#E8F5E9]"><div className="h-full rounded-full bg-[#4CAF50] transition-[width] duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div></div>
          {step === 0 ? <div className="grid gap-4 md:grid-cols-2"><FormField label="Full name" testId="intake-full-name-field"><Input value={form.full_name} onChange={(event) => updateField("full_name", event.target.value)} data-testid="intake-full-name-input" /></FormField><FormField label="Age" testId="intake-age-field"><Input value={form.age} onChange={(event) => updateField("age", event.target.value)} data-testid="intake-age-input" /></FormField><div className="md:col-span-2"><p className="mb-3 text-sm font-semibold text-slate-700">Gender</p><ChoiceGrid options={GENDER_OPTIONS} selected={form.gender} onToggle={(value) => updateField("gender", value)} testPrefix="intake-gender-option" /></div><FormField label="Weight (kg)" testId="intake-weight-field"><Input value={form.weight} onChange={(event) => updateField("weight", event.target.value)} data-testid="intake-weight-input" /></FormField><FormField label="Height (cm)" testId="intake-height-field"><Input value={form.height} onChange={(event) => updateField("height", event.target.value)} data-testid="intake-height-input" /></FormField><FormField label="Occupation" testId="intake-occupation-field"><Input value={form.occupation} onChange={(event) => updateField("occupation", event.target.value)} data-testid="intake-occupation-input" /></FormField></div> : null}
          {step === 1 ? <div className="space-y-6"><div><p className="mb-3 text-sm font-semibold text-slate-700">Health goals</p><ChoiceGrid options={GOAL_OPTIONS} selected={form.health_goals} onToggle={toggleGoal} multi testPrefix="intake-goal-option" /></div><div><p className="mb-3 text-sm font-semibold text-slate-700">Diet type</p><ChoiceGrid options={DIET_OPTIONS} selected={form.diet_type} onToggle={(value) => updateField("diet_type", value)} testPrefix="intake-diet-option" /></div></div> : null}
          {step === 2 ? <div className="grid gap-4 md:grid-cols-2"><FormField label="Health conditions" testId="intake-health-conditions-field"><Textarea value={form.health_conditions} onChange={(event) => updateField("health_conditions", event.target.value)} data-testid="intake-health-conditions-input" /></FormField><FormField label="Current medications" testId="intake-current-medications-field"><Textarea value={form.current_medications} onChange={(event) => updateField("current_medications", event.target.value)} data-testid="intake-current-medications-input" /></FormField><FormField label="Food allergies" testId="intake-food-allergies-field"><Textarea value={form.food_allergies} onChange={(event) => updateField("food_allergies", event.target.value)} data-testid="intake-food-allergies-input" /></FormField><FormField label="Food dislikes" testId="intake-food-dislikes-field"><Textarea value={form.food_dislikes} onChange={(event) => updateField("food_dislikes", event.target.value)} data-testid="intake-food-dislikes-input" /></FormField></div> : null}
          {step === 3 ? <div className="grid gap-4 md:grid-cols-2"><div className="md:col-span-2"><p className="mb-3 text-sm font-semibold text-slate-700">Activity level</p><ChoiceGrid options={ACTIVITY_OPTIONS} selected={form.activity_level} onToggle={(value) => updateField("activity_level", value)} testPrefix="intake-activity-option" /></div><FormField label="Morning beverage preference" testId="intake-morning-beverage-field"><Input value={form.morning_beverage_preference} onChange={(event) => updateField("morning_beverage_preference", event.target.value)} data-testid="intake-morning-beverage-input" /></FormField><div className="space-y-2" data-testid="intake-meals-skipped-field"><p className="text-sm font-semibold text-slate-700">Number of times meals skipped</p><RadioChoiceList options={MEALS_SKIPPED_OPTIONS} selected={form.meals_skipped} onChange={(value) => updateField("meals_skipped", value)} name="meals-skipped" testPrefix="intake-meals-skipped-option" /></div><FormField label="Uric acid" testId="intake-uric-acid-field"><Input value={form.uric_acid} onChange={(event) => updateField("uric_acid", event.target.value)} data-testid="intake-uric-acid-input" /></FormField><FormField label="HbA1c" testId="intake-hba1c-field"><Input value={form.hba1c} onChange={(event) => updateField("hba1c", event.target.value)} data-testid="intake-hba1c-input" /></FormField><FormField label="Fasting blood sugar" testId="intake-fasting-blood-sugar-field"><Input value={form.fasting_blood_sugar} onChange={(event) => updateField("fasting_blood_sugar", event.target.value)} data-testid="intake-fasting-blood-sugar-input" /></FormField><FormField label="TSH" testId="intake-tsh-field"><Input value={form.tsh} onChange={(event) => updateField("tsh", event.target.value)} data-testid="intake-tsh-input" /></FormField><FormField label="Meal timing preference" testId="intake-meal-timing-field"><Input value={form.meal_timing_preference} onChange={(event) => updateField("meal_timing_preference", event.target.value)} data-testid="intake-meal-timing-input" /></FormField><FormField label="Water intake (litres/day)" testId="intake-water-intake-field"><Input value={form.water_intake} onChange={(event) => updateField("water_intake", event.target.value)} data-testid="intake-water-intake-input" /></FormField></div> : null}
          <div className="flex flex-wrap justify-between gap-3"><Button variant="outline" className="rounded-full border-[#D1D5DB]" onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0} data-testid="intake-previous-step-button">Previous</Button><div className="flex gap-3">{step < steps.length - 1 ? <Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => setStep((current) => Math.min(current + 1, steps.length - 1))} data-testid="intake-next-step-button">Next</Button> : <Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={submit} disabled={loading} data-testid="intake-submit-button">{loading ? "Saving..." : "Save intake form"}</Button>}</div></div>
        </CardContent>
      </Card>
    </div>
  );
};

const UploadPanel = ({ title, description, onSubmit, children, submitLabel, testId }) => (
  <Card className="border-[#D1D5DB] shadow-sm" data-testid={testId}>
    <CardHeader><CardTitle className="text-xl text-slate-900">{title}</CardTitle><p className="text-sm leading-7 text-slate-600">{description}</p></CardHeader>
    <CardContent>{children}<Button className="mt-5 rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={onSubmit} data-testid={`${testId}-submit-button`}>{submitLabel}</Button></CardContent>
  </Card>
);

const ClientMealLogPage = () => {
  const { authRequest } = useAuth();
  const [logs, setLogs] = useState([]);
  const [file, setFile] = useState(null);
  const [mealType, setMealType] = useState("Breakfast");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const load = useCallback(async () => { const response = await authRequest("get", "/client/meal-logs"); setLogs(response.meal_logs || []); }, [authRequest]);
  useEffect(() => { load().catch(() => toast.error("Unable to load meal logs")); }, [load]);

  const submit = async () => {
    if (!file) return toast.error("Please select a meal photo");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("meal_type", mealType);
      formData.append("note", note);
      formData.append("file", file);
      await authRequest("post", "/client/meal-logs", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Meal photo uploaded");
      setFile(null);
      setNote("");
      setFileInputKey((current) => current + 1);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to upload meal photo right now");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Meal log" title="Upload your meal photos" description="Share meals from camera or file upload so your nutritionist can review them with AI-assisted estimates." />
      <UploadPanel title="New meal upload" description="Add the meal type, a short note and the photo file." onSubmit={submit} submitLabel="Upload meal photo" testId="client-meal-upload-panel">
        <div className="grid gap-4 md:grid-cols-2"><FormField label="Meal type" testId="meal-log-type-field"><ChoiceGrid options={MEAL_TYPES} selected={mealType} onToggle={setMealType} testPrefix="meal-type-option" /></FormField><FormField label="Short note" testId="meal-log-note-field"><Textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Example: paneer wrap, fruit bowl, dal rice" data-testid="meal-log-note-input" /></FormField><FormField label="Meal photo" testId="meal-log-file-field"><input key={fileInputKey} type="file" accept="image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} className="rounded-2xl border border-dashed border-[#D1D5DB] bg-[#F8FAF8] p-4 text-sm" data-testid="meal-log-file-input" /></FormField><div className="text-sm text-slate-500" data-testid="meal-log-selected-file-label">{file ? `Selected file: ${file.name}` : "No file selected yet."}</div></div>
      </UploadPanel>
      {uploading ? <p className="text-sm font-semibold text-[#2E7D32]" data-testid="meal-log-uploading-state">Uploading your meal photo...</p> : null}
      <div className="grid gap-4 lg:grid-cols-2">{logs.map((item) => <Card key={item.meal_log_id} className="border-[#D1D5DB] shadow-sm" data-testid={`client-meal-log-item-${item.meal_log_id}`}><CardContent className="space-y-4 p-6">{item.image_url ? <div className="overflow-hidden rounded-3xl border border-[#D1D5DB] bg-[#F8FAF8] p-3"><img src={item.image_url} alt={item.meal_type} className="h-64 w-full object-contain" data-testid={`client-meal-log-image-${item.meal_log_id}`} /></div> : null}<div className="flex items-center justify-between"><p className="text-lg font-semibold text-slate-900">{item.meal_type}</p><p className="text-sm text-slate-500">{friendlyDate(item.created_at)}</p></div><p className="text-sm leading-7 text-slate-600">{item.ai_analysis?.summary}</p><div className="flex flex-wrap gap-2"><Badge variant="outline">{item.ai_analysis?.calories} kcal</Badge><Badge variant="outline">Protein {item.ai_analysis?.protein}g</Badge><Badge variant="outline">Carbs {item.ai_analysis?.carbs}g</Badge><Badge variant="outline">Fat {item.ai_analysis?.fat}g</Badge></div><div className="flex flex-wrap gap-3"><a href={item.image_url} download={`${item.meal_type || 'meal-photo'}.png`} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`client-meal-log-download-${item.meal_log_id}`}>Download photo</a><a href={item.image_url} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`client-meal-log-open-${item.meal_log_id}`}>Open photo</a></div></CardContent></Card>)}</div>
    </div>
  );
};

const ClientBloodWorkPage = () => {
  const { authRequest } = useAuth();
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const load = useCallback(async () => { const response = await authRequest("get", "/client/blood-work"); setItems(response.blood_works || []); }, [authRequest]);
  useEffect(() => { load().catch(() => toast.error("Unable to load blood work")); }, [load]);

  const submit = async () => {
    if (!file) return toast.error("Please select a report file");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("note", note);
      formData.append("file", file);
      await authRequest("post", "/client/blood-work", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Blood report uploaded");
      setFile(null);
      setNote("");
      setFileInputKey((current) => current + 1);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to upload blood report right now");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Blood work" title="Upload your reports" description="Upload PDFs or images so extracted markers and the nutrition summary can feed into your plan." />
      <UploadPanel title="New blood report" description="Upload a PDF or image. You can also add short notes for context." onSubmit={submit} submitLabel="Upload blood work" testId="client-blood-work-upload-panel">
        <div className="grid gap-4 md:grid-cols-2"><FormField label="Report note" testId="blood-work-note-field"><Textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Example: Jan annual test" data-testid="blood-work-note-input" /></FormField><FormField label="Report file" testId="blood-work-file-field"><input key={fileInputKey} type="file" accept="image/*,.pdf" onChange={(event) => setFile(event.target.files?.[0] || null)} className="rounded-2xl border border-dashed border-[#D1D5DB] bg-[#F8FAF8] p-4 text-sm" data-testid="blood-work-file-input" /></FormField><div className="text-sm text-slate-500" data-testid="blood-work-selected-file-label">{file ? `Selected file: ${file.name}` : "No file selected yet."}</div></div>
      </UploadPanel>
      {uploading ? <p className="text-sm font-semibold text-[#2E7D32]" data-testid="blood-work-uploading-state">Uploading your blood report...</p> : null}
      <div className="grid gap-4">{items.map((item) => <Card key={item.blood_work_id} className="border-[#D1D5DB] shadow-sm" data-testid={`client-blood-work-item-${item.blood_work_id}`}><CardContent className="space-y-4 p-6"><div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><div><p className="text-lg font-semibold text-slate-900">{item.file_name}</p><p className="text-sm text-slate-500">{friendlyDate(item.created_at)}</p></div><div className="flex flex-wrap gap-3"><StatusPill value={item.ai_analysis?.mode} testId={`client-blood-work-mode-${item.blood_work_id}`} /><a href={item.file_url} download={item.file_name || "blood-report"} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full border border-[#D1D5DB] px-4 py-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-[#4CAF50] hover:text-[#2E7D32]" data-testid={`client-blood-work-download-${item.blood_work_id}`}>Download report</a></div></div><p className="text-sm leading-7 text-slate-600">{item.ai_analysis?.ai_summary}</p><div className="flex flex-wrap gap-2">{Object.entries(item.extracted_values || {}).map(([key, value]) => <Badge key={key} variant="outline" data-testid={`client-blood-value-${item.blood_work_id}-${key}`}>{key}: {value}</Badge>)}</div></CardContent></Card>)}</div>
    </div>
  );
};

const ClientChatPage = () => {
  const { authRequest, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [nutritionist, setNutritionist] = useState(null);
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => { const response = await authRequest("get", "/client/chat"); setMessages(response.messages || []); setNutritionist(response.nutritionist || null); }, [authRequest]);
  useEffect(() => { load().catch(() => toast.error("Unable to load chat")); }, [load]);
  const sendMessage = async (content) => { setSending(true); try { await authRequest("post", "/client/chat", { content }); await load(); } finally { setSending(false); } };
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Chat" title="Message your nutritionist" description={`Stay in touch with ${nutritionist?.name || "your nutritionist"} for reminders, feedback and follow-ups.`} />
      <ChatPanel messages={messages} currentUserId={user.user_id} onSend={sendMessage} sending={sending} placeholder="Share a question or update" />
    </div>
  );
};

const ClientProfilePage = () => {
  const { authRequest } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "" });
  useEffect(() => { authRequest("get", "/client/profile").then((response) => setForm(response.profile)).catch(() => toast.error("Unable to load profile")); }, [authRequest]);
  const save = async () => { await authRequest("put", "/client/profile", { name: form.name, phone: form.phone }); toast.success("Profile updated"); };
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Profile" title="Your personal details" description="Update your name and phone number here. Use the logout button in the header whenever you finish." />
      <Card className="border-[#D1D5DB] shadow-sm" data-testid="client-profile-card"><CardContent className="grid gap-4 p-6 md:grid-cols-2"><FormField label="Full name" testId="client-profile-name-field"><Input value={form.name || ""} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} data-testid="client-profile-name-input" /></FormField><FormField label="Phone" testId="client-profile-phone-field"><Input value={form.phone || ""} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} data-testid="client-profile-phone-input" /></FormField><div className="md:col-span-2"><Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047]" onClick={save} data-testid="client-profile-save-button">Save profile</Button></div></CardContent></Card>
    </div>
  );
};

const App = () => (
  <div className="min-h-screen bg-[#F8FAF8] text-slate-900">
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminLayout><AdminDashboardPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminLayout><ClientsPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/clients/:id" element={<ProtectedRoute role="admin"><AdminLayout><ClientDetailPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/chat" element={<ProtectedRoute role="admin"><AdminLayout><AdminChatPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/nudge" element={<ProtectedRoute role="admin"><AdminLayout><AdminNudgePage /></AdminLayout></ProtectedRoute>} />
          <Route path="/client/home" element={<ProtectedRoute role="client"><ClientLayout><ClientHomePage /></ClientLayout></ProtectedRoute>} />
          <Route path="/client/intake-form" element={<ProtectedRoute role="client"><ClientLayout><ClientIntakeFormPage /></ClientLayout></ProtectedRoute>} />
          <Route path="/client/meal-log" element={<ProtectedRoute role="client"><ClientLayout><ClientMealLogPage /></ClientLayout></ProtectedRoute>} />
          <Route path="/client/blood-work" element={<ProtectedRoute role="client"><ClientLayout><ClientBloodWorkPage /></ClientLayout></ProtectedRoute>} />
          <Route path="/client/chat" element={<ProtectedRoute role="client"><ClientLayout><ClientChatPage /></ClientLayout></ProtectedRoute>} />
          <Route path="/client/profile" element={<ProtectedRoute role="client"><ClientLayout><ClientProfilePage /></ClientLayout></ProtectedRoute>} />
        </Routes>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  </div>
);

export default App;
